import { create } from 'zustand';

export interface LiveCommittedTurn {
  turnOrder: number;
  text: string;
  speakerLabel: string | null;
  /** Milliseconds from the start of the audio stream to this turn's first
   *  word. Null when the turn arrived without word timings. */
  startMs: number | null;
}

/** Payload shape from the `recording://transcript` event. */
export interface TranscriptIngestPayload {
  recordingId: string;
  activeTurn: string | null;
  committed: {
    turnOrder: number;
    text: string;
    speakerLabel: string | null;
    startMs?: number | null;
  }[];
}

interface LiveTranscriptState {
  /** The recording these captions belong to, or null when idle. */
  recordingId: string | null;
  /** Current provisional (not-yet-finalized) turn text. */
  activeTurn: string | null;
  /** Finalized turns, deduped by turnOrder, sorted ascending. Ephemeral. */
  committed: LiveCommittedTurn[];
  /** True once at least one event has arrived (left the "Listening…" state). */
  started: boolean;
  /**
   * Ingest one transcript event. Only ONE recording is ever active (enforced by
   * `recording_start`'s AlreadyRecording guard), so a different `recordingId`
   * means a NEW meeting — the store resets before accumulating, dropping the
   * previous meeting's ephemeral captions (the batch pass is authoritative).
   */
  ingest: (recordingId: string, payload: TranscriptIngestPayload) => void;
  /** Reset to the idle "Listening…" state. */
  clear: () => void;
}

const EMPTY = {
  recordingId: null as string | null,
  activeTurn: null as string | null,
  committed: [] as LiveCommittedTurn[],
  started: false,
};

export const useLiveTranscriptStore = create<LiveTranscriptState>((set) => ({
  ...EMPTY,

  ingest: (recordingId, payload) =>
    set((prev) => {
      // New recording → start fresh (drop the prior meeting's captions).
      const base = prev.recordingId === recordingId ? prev.committed : [];

      const byOrder = new Map(base.map((t) => [t.turnOrder, t]));
      for (const t of payload.committed) {
        byOrder.set(t.turnOrder, {
          turnOrder: t.turnOrder,
          text: t.text,
          speakerLabel: t.speakerLabel ?? null,
          startMs: t.startMs ?? null,
        });
      }
      const committed = [...byOrder.values()].sort((a, b) => a.turnOrder - b.turnOrder);

      return {
        recordingId,
        activeTurn: payload.activeTurn ?? null,
        committed,
        started: true,
      };
    }),

  clear: () => set({ ...EMPTY }),
}));

export function useHasLiveCaptions(recordingId: string | null): boolean {
  return useLiveTranscriptStore(
    (s) =>
      recordingId !== null &&
      s.recordingId === recordingId &&
      (s.committed.length > 0 || s.activeTurn !== null),
  );
}
